import React, { useState } from "react";

// The billing address a US sales tax is worked out from (memberships and licences). The state and ZIP are checked while the
// person types; the server checks them again. Outside the United States no sales tax is charged, so only the 2-letter
// country code is asked for.
export const US_STATES = [
  ["AL", "Alabama"], ["AK", "Alaska"], ["AZ", "Arizona"], ["AR", "Arkansas"], ["CA", "California"], ["CO", "Colorado"],
  ["CT", "Connecticut"], ["DE", "Delaware"], ["DC", "District of Columbia"], ["FL", "Florida"], ["GA", "Georgia"],
  ["HI", "Hawaii"], ["ID", "Idaho"], ["IL", "Illinois"], ["IN", "Indiana"], ["IA", "Iowa"], ["KS", "Kansas"],
  ["KY", "Kentucky"], ["LA", "Louisiana"], ["ME", "Maine"], ["MD", "Maryland"], ["MA", "Massachusetts"],
  ["MI", "Michigan"], ["MN", "Minnesota"], ["MS", "Mississippi"], ["MO", "Missouri"], ["MT", "Montana"],
  ["NE", "Nebraska"], ["NV", "Nevada"], ["NH", "New Hampshire"], ["NJ", "New Jersey"], ["NM", "New Mexico"],
  ["NY", "New York"], ["NC", "North Carolina"], ["ND", "North Dakota"], ["OH", "Ohio"], ["OK", "Oklahoma"],
  ["OR", "Oregon"], ["PA", "Pennsylvania"], ["RI", "Rhode Island"], ["SC", "South Carolina"], ["SD", "South Dakota"],
  ["TN", "Tennessee"], ["TX", "Texas"], ["UT", "Utah"], ["VT", "Vermont"], ["VA", "Virginia"], ["WA", "Washington"],
  ["WV", "West Virginia"], ["WI", "Wisconsin"], ["WY", "Wyoming"],
];
const ZIP = /^\d{5}(-\d{4})?$/;

export const EMPTY_ADDRESS = { country: "US", state: "", postal_code: "" };

// What the server sent back (or nothing) as the starting value.
export const addressFrom = (saved) => (saved && saved.country ? { country: saved.country, state: saved.state || "", postal_code: saved.postal_code || "" } : EMPTY_ADDRESS);

// One message per field that is not right yet. An empty object means the address can be sent.
export const addressProblems = (a) => {
  const problems = {};
  if (a.country === "US") {
    if (!US_STATES.some(([code]) => code === a.state)) problems.state = "Choose a state.";
    if (!a.postal_code.trim()) problems.postal_code = "Enter your ZIP code.";
    else if (!ZIP.test(a.postal_code.trim())) problems.postal_code = "Enter a valid ZIP code, for example 94105.";
  } else if (!/^[A-Za-z]{2}$/.test(a.country)) {
    problems.country = "Enter the 2-letter country code, for example GB.";
  }
  return problems;
};

// The body sent to the server, or undefined while the address is not complete.
export const addressPayload = (a) => (Object.keys(addressProblems(a)).length ? undefined : { ...a, state: a.state || undefined, postal_code: a.postal_code.trim() || undefined, country: a.country.toUpperCase() });

const field = "w-full border border-gray-300 rounded-lg px-3 py-2.5 bg-white";

export default function BillingAddressFields({ value, onChange, showAll = false, disabled = false, idPrefix = "billing" }) {
  const [touched, setTouched] = useState({});
  const problems = addressProblems(value);
  const shown = (name) => (showAll || touched[name] ? problems[name] : "");
  const touch = (name) => setTouched((t) => ({ ...t, [name]: true }));
  const us = value.country === "US";

  return (
    <fieldset className="mt-5" data-testid="billing-address">
      <legend className="text-sm font-semibold text-gray-900">Billing address</legend>
      <p className="text-xs text-gray-500 mt-0.5">Used to work out US sales tax.</p>
      <div className="mt-2">
        <label className="block text-sm text-gray-600 mb-1" htmlFor={`${idPrefix}-country`}>Country</label>
        <select
          id={`${idPrefix}-country`} className={field} disabled={disabled}
          value={us ? "US" : "OTHER"}
          onChange={(e) => onChange(e.target.value === "US" ? { ...EMPTY_ADDRESS } : { country: "", state: "", postal_code: "" })}
        >
          <option value="US">United States</option>
          <option value="OTHER">Outside the United States</option>
        </select>
      </div>
      {us ? (
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <label className="block text-sm text-gray-600 mb-1" htmlFor={`${idPrefix}-state`}>State</label>
            <select
              id={`${idPrefix}-state`} className={field} disabled={disabled} value={value.state}
              aria-invalid={Boolean(shown("state"))} aria-describedby={shown("state") ? `${idPrefix}-state-error` : undefined}
              onChange={(e) => { touch("state"); onChange({ ...value, state: e.target.value }); }} onBlur={() => touch("state")}
            >
              <option value="">Select state</option>
              {US_STATES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
            </select>
            {shown("state") && <p id={`${idPrefix}-state-error`} className="text-sm text-red-600 mt-1" role="alert">{problems.state}</p>}
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1" htmlFor={`${idPrefix}-zip`}>ZIP code</label>
            <input
              id={`${idPrefix}-zip`} type="text" inputMode="numeric" autoComplete="postal-code" maxLength={10} placeholder="94105"
              className={field} disabled={disabled} value={value.postal_code}
              aria-invalid={Boolean(shown("postal_code"))} aria-describedby={shown("postal_code") ? `${idPrefix}-zip-error` : undefined}
              onChange={(e) => { touch("postal_code"); onChange({ ...value, postal_code: e.target.value.replace(/[^\d-]/g, "").slice(0, 10) }); }}
              onBlur={() => touch("postal_code")}
            />
            {shown("postal_code") && <p id={`${idPrefix}-zip-error`} className="text-sm text-red-600 mt-1" role="alert">{problems.postal_code}</p>}
          </div>
        </div>
      ) : (
        <div className="mt-3">
          <label className="block text-sm text-gray-600 mb-1" htmlFor={`${idPrefix}-code`}>Country code</label>
          <input
            id={`${idPrefix}-code`} type="text" maxLength={2} placeholder="GB" className={`${field} uppercase`} disabled={disabled} value={value.country}
            aria-invalid={Boolean(shown("country"))} aria-describedby={shown("country") ? `${idPrefix}-code-error` : undefined}
            onChange={(e) => { touch("country"); onChange({ country: e.target.value.replace(/[^A-Za-z]/g, "").toUpperCase(), state: "", postal_code: "" }); }}
            onBlur={() => touch("country")}
          />
          {shown("country") && <p id={`${idPrefix}-code-error`} className="text-sm text-red-600 mt-1" role="alert">{problems.country}</p>}
          <p className="text-xs text-gray-500 mt-1">No US sales tax is charged outside the United States.</p>
        </div>
      )}
    </fieldset>
  );
}
